import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import API from '../../../../app/api';
import {setSnackbar} from '../../../../store/reducers/globals';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {mapHospitalList, multiHospitalMode, wardsList} from '../../../../store/reducers/ward';
import Chip from '@mui/material/Chip';
import {Box} from '@mui/material';
import clsx from 'clsx';
import {getWardsIdForPatientsSearch} from '../../../../store/reducers/wardMap';
import {ctxSplitScreen} from '../../../../store/reducers/patientContext';
import {getLocationChipColor, MAP_TYPE} from '../../../../util/mapCreator/mapUtils';
import {useTheme} from '@mui/material/styles';
import {useStylesBedRequest} from './bedRequestStyle';

export const WARD_SELECT_TYPE = {
    ALL: 'ALL',
    INPATIENT: MAP_TYPE.INPATIENT,
    ED: MAP_TYPE.ED,
};

const BedRequestWardSelect = ({
                                  form,
                                  setForm,
                                  setSaveData,
                                  onChangeWard,
                                  ignoreAllocated = false,
                                  modifiedKey = null,
                                  onlyWard = false,
                                  withNewBackground = false,
                                  disabled,
                                  filterMap,
                                  wardSelectType = WARD_SELECT_TYPE.ALL,
                                  onlyHospital,
                              }) => {
    const classes = useStylesBedRequest();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const controller = new AbortController();
    const theme = useTheme();

    // Selectors
    const wardList = useSelector(wardsList);
    const isMultiHospitalMode = useSelector(multiHospitalMode);
    const hospitalList = useSelector(mapHospitalList);
    const splitScreen = useSelector(ctxSplitScreen);

    // Selected Wards
    const [wards, setWards] = useState([]);
    const [bays, setBays] = useState([]);
    const [baysLoading, setBaysLoading] = useState(false);
    const [beds, setBeds] = useState([]);
    const [bedsLoading, setBedsLoading] = useState(false);
    const [escalationLevel, setEscalationLevel] = useState([]);

    // Availability
    const [patientsInWard, setPatientsInWard] = useState([]);
    const [bedStatuses, setBedStatuses] = useState(null);
    const [bedRequests, setBedRequests] = useState([]);

    // Others
    const isChecked = ignoreAllocated ? true : form?.allocated?.checked;
    const modifiedStateKey = modifiedKey || 'destination';

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (onlyHospital) {
            setForm(prevState => ({
                ...prevState,
                [modifiedStateKey]: {
                    ...prevState?.[modifiedStateKey],
                    hospitalId: onlyHospital,
                    wardId: null,
                    wardName: null,
                    bedId: null,
                    bedName: null,
                    bayId: null,
                    bayName: null,
                },
            }));
        }
    }, [onlyHospital]);

    // Set ward and load escalation level for this ward
    useEffect(() => {
        let filtered = _.filter(wardList, w => w.active && w.wardsId.length === 1);
        if (filterMap) {
            filtered = _.filter(filtered, w => w?.__id !== filterMap);
        }

        if (wardSelectType !== WARD_SELECT_TYPE.ALL) {
            filtered = _.filter(filtered, w => w.type === wardSelectType);
        }

        setWards(_.sortBy(filtered, s => s.wardsId[0]));

        API.getEscalationLevels()
            .then(res => setEscalationLevel(res))
            .catch((e) => {
                console.error(e);
                dispatch(setSnackbar({type: 'error', message: t('Unable to retrieve escalation levels.')}));
            });
    }, [wardList, filterMap, wardSelectType]);

    useEffect(() => {
        if (form?.[modifiedStateKey]?.wardId) {
            setBaysLoading(true);
            API.getAllAreas(form?.[modifiedStateKey]?.wardId, controller.signal)
                .then(zones => {
                    const hospitalId = form?.[modifiedStateKey]?.hospitalId;
                    const filteredBays = _.filter(zones?.data, bay => !bay?.envObject && bay?.type !== 'list' && (hospitalId ? bay?.hospitalId === hospitalId : true) && !_.isEmpty(bay?.linkedBeds));
                    setBays(filteredBays || []);
                })
                .catch(err => {
                    console.error(err);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: `There was a problem getting bays for ward id ${form?.[modifiedStateKey]?.wardId}`,
                    }));
                })
                .finally(() => {
                    setBaysLoading(false);
                });
        }
    }, [form?.[modifiedStateKey]?.wardId]);

    useEffect(() => {
        const loadingBedsAndDataWard = async (selectedZone) => {
            const wardId = form?.[modifiedStateKey]?.wardId;
            await API.getAllBeds(wardId, controller.signal)
                .then(bed => {
                    let filtered = _.filter(bed?.data, b => !b.archived && selectedZone?.linkedBeds?.includes(b?.__id));
                    if (!_.isEmpty(filtered)) {
                        const escalationLevelWard = _.find(escalationLevel, {wardId: filtered[0]?.wardId})?.level || 'core';
                        filtered = filterBedsByEscalationLevel(filtered, escalationLevelWard);
                    }
                    setBeds(filtered || []);
                })
                .catch(err => {
                    console.error(err);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: `There was a problem getting space for ward id ${form?.[modifiedStateKey]?.wardId}`,
                    }));
                });
            const wardIds = dispatch(getWardsIdForPatientsSearch([wardId], form?.[modifiedStateKey]?.hospitalId));
            await API.getWardData(wardIds[0])
                .then((res) => {
                    if (res?.bedStatuses)
                        setBedStatuses(res.bedStatuses);
                })
                .catch(err => {
                    console.error(err);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: `There was a problem getting beds status for ward id ${form?.[modifiedStateKey]?.wardId}`,
                    }));
                    setBedStatuses(null);
                });

            await API.getWardsAttendances([wardId])
                .then((res) => {
                    if (res)
                        setPatientsInWard(res || []);
                })
                .catch(err => {
                    console.error(err);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: `There was a problem getting patients for ward id ${form?.[modifiedStateKey]?.wardId}`,
                    }));
                    setPatientsInWard([]);
                });

            await API.getBedRequest(`status eq 'NEW'`)
                .then((res) => {
                    if (res) {
                        const filtered = _.filter(res?.__data, br => !br.followUp && br?.destination?.wardId === wardId && !br?.canceled?.checked);
                        setBedRequests(filtered || []);
                    }

                })
                .catch(err => {
                    console.error(err);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: `There was a problem getting bed requests for ward id ${form?.[modifiedStateKey]?.wardId}`,
                    }));
                    setBedRequests([]);
                });

            setBedsLoading(false);
        };


        if (!_.isEmpty(bays) && form?.[modifiedStateKey]?.bayId) {
            setBedsLoading(true);
            const selectedZone = _.find(bays, {__id: form?.[modifiedStateKey]?.bayId});
            loadingBedsAndDataWard(selectedZone);
        }
    }, [bays, form?.[modifiedStateKey]?.bayId]);

    const handleChangeHospital = (_e, value) => {
        setForm(prevState => ({
            ...prevState,
            [modifiedStateKey]: {
                ...prevState?.[modifiedStateKey],
                hospitalId: value?.__id || null,
                wardId: null,
                wardName: null,
                bedId: null,
                bedName: null,
                bayId: null,
                bayName: null,
            },
        }));
        if (setSaveData) setSaveData(true);
    };

    const handleChangeWard = (_e, value) => {
        if (onChangeWard) {
            onChangeWard(value);
        }
        setForm(prevState => ({
            ...prevState,
            [modifiedStateKey]: {
                ...prevState?.[modifiedStateKey],
                wardId: value?.wardsId[0] || null,
                wardName: value?.name || null,
                bedId: null,
                bedName: null,
                bayId: null,
                bayName: null,
            },
        }));
        if (setSaveData) setSaveData(true);
    };

    const handleChangeZone = (_e, value) => {
        setForm(prevState => ({
            ...prevState,
            [modifiedStateKey]: {
                ...prevState?.[modifiedStateKey],
                bayId: value?.__id || null,
                bayName: value?.name || null,
                bedId: null,
                bedName: null,
            },
        }));
        if (setSaveData) setSaveData(true);
    };

    const handleChangeBed = (_e, value) => {
        setForm(prevState => ({
            ...prevState,
            [modifiedStateKey]: {
                ...prevState?.[modifiedStateKey],
                bedId: value?.__id || null,
                bedName: value?.name || null,
            },
        }));
        if (setSaveData) setSaveData(true);
    };

    const getValue = (type) => {
        switch (type) {
            case 'ward': {
                if (!form?.[modifiedStateKey]?.wardId) return null;

                const find = _.find(wards, w => w.wardsId[0] === form?.[modifiedStateKey]?.wardId);
                if (find) return find;
                return {
                    __id: form?.[modifiedStateKey]?.wardId,
                    wardsId: [form?.[modifiedStateKey]?.wardId],
                    name: form?.[modifiedStateKey]?.wardName,
                };
            }
            case 'bay': {
                if (!form?.[modifiedStateKey]?.bayId && !form?.[modifiedStateKey]?.bayName) return null;
                return {
                    __id: form?.[modifiedStateKey]?.bayId,
                    name: form?.[modifiedStateKey]?.bayName,
                };
            }
            case 'bed': {
                if (!form?.[modifiedStateKey]?.bedId && !form?.[modifiedStateKey]?.bedName) return null;
                return {
                    __id: form?.[modifiedStateKey]?.bedId,
                    name: form?.[modifiedStateKey]?.bedName,
                };
            }
            case 'hospital': {
                if (!form?.[modifiedStateKey]?.hospitalId) return null;
                const findFacility = _.find(hospitalList, {__id: form?.[modifiedStateKey]?.hospitalId});
                return findFacility || null;
            }
            default:
                return null;
        }
    };

    const filterWards = () => {
        if (isMultiHospitalMode) {
            const hospitalId = form?.[modifiedStateKey]?.hospitalId;
            return _.filter(wards, {hospitalId});
        }
        return wards;
    };

    const getBedIndicator = useCallback((bedId, checkAvailability) => {
        const isOccupied = _.some(patientsInWard, pt => pt?.bed === bedId);
        const isReserved = _.some(bedRequests, br => br?.destination?.bedId === bedId);
        const haveStatus = !!bedStatuses?.[bedId];
        const obj = _.find(beds, {__id: bedId});
        const isDisabled = obj && !obj?.available;

        if (checkAvailability) return !isReserved && !isDisabled;

        return (
            <div className={clsx(
                classes.box,
                (isReserved || isOccupied || isDisabled) && 'occupied',
                haveStatus && 'status',
            )}>
                {(isReserved || isDisabled) ? 'X' : ''}
            </div>
        );
    }, [beds, patientsInWard, bedRequests, bedStatuses]);

    return (
        <div className={classes.bedAllocation}>
            {isMultiHospitalMode &&
                <div className={classes.divRow} style={{width: '100%'}}>
                    <AgyleDictionaryField
                        label={t('Hospital')}
                        onChange={handleChangeHospital}
                        options={hospitalList}
                        value={getValue('hospital')}
                        getOptionLabel={option => option.description}
                        isOptionEqualToValue={(option, value) => option.__id === value.__id}
                        disabled={Boolean(!isChecked || disabled || onlyHospital)}
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Chip
                                    label={option?.__id || 'UNK'}
                                    style={{marginRight: 10, width: 80}}
                                    size="small"
                                />
                                {option.description}
                            </Box>
                        )}
                        fullWidth
                        size={withNewBackground ? 'large' : 'small'}
                        noBorder={!withNewBackground}
                        withNewBackground={withNewBackground}
                    />
                </div>
            }
            <div className={clsx(classes.wards, splitScreen?.enabled && 'column')}>
                <AgyleDictionaryField
                    label={t('Ward')}
                    onChange={handleChangeWard}
                    options={filterWards()}
                    value={getValue('ward')}
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, value) => option.__id === value.__id}
                    disabled={Boolean(!isChecked || disabled || (isMultiHospitalMode && !form?.[modifiedStateKey]?.hospitalId))}
                    renderOption={(props, option) => (
                        <Box component="li" {...props}>
                            <Chip
                                label={option.wardsId[0]}
                                style={{
                                    marginRight: 10,
                                    minWidth: 100,
                                    ...getLocationChipColor(theme, option),
                                    fontWeight: 'bold',
                                }}
                                size="small"
                            />
                            {option.name}
                        </Box>
                    )}
                    size={withNewBackground ? 'large' : 'small'}
                    noBorder={!withNewBackground}
                    withNewBackground={withNewBackground}
                />

                {!onlyWard && (
                    <>
                        <AgyleDictionaryField
                            label={t('Bay')}
                            onChange={handleChangeZone}
                            options={bays}
                            loading={baysLoading}
                            value={getValue('bay')}
                            getOptionLabel={option => option.name}
                            isOptionEqualToValue={(option, value) => option.__id === value.__id}
                            disabled={Boolean(!isChecked || !form?.[modifiedStateKey]?.wardId || onlyWard || disabled)}
                            size={withNewBackground ? 'large' : 'small'}
                            noBorder={!withNewBackground}
                            withNewBackground={withNewBackground}
                        />

                        <AgyleDictionaryField
                            label={t('Space')}
                            onChange={handleChangeBed}
                            options={beds}
                            loading={bedsLoading}
                            value={getValue('bed')}
                            getOptionLabel={option => option.name}
                            isOptionEqualToValue={(option, value) => option.__id === value.__id}
                            disabled={Boolean(!isChecked || !form?.[modifiedStateKey]?.bayId || onlyWard || disabled)}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    {getBedIndicator(option?.__id)}
                                    {option.name}
                                </Box>
                            )}
                            getOptionDisabled={option => !getBedIndicator(option?.__id, true)}
                            size={withNewBackground ? 'large' : 'small'}
                            noBorder={!withNewBackground}
                            withNewBackground={withNewBackground}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default BedRequestWardSelect;

export const filterBedsByEscalationLevel = (beds, level) => {
    switch (level) {
        case 'core':
            return _.filter(beds, bed => bed?.subtype === 'core');
        case 'planned':
            return _.filter(beds, bed => bed?.subtype === 'core' || bed?.subtype === 'planned');
        case 'unplanned':
        default:
            return beds;
    }
};